import EventEmitter from 'eventemitter3';
import { filter } from 'lodash';
import type { CanvasEvents } from './CanvasEvent';
import type SurfaceBase from './SurfaceBase';
import type { Point } from './SurfaceBase';

export default abstract class CanvasObject extends EventEmitter<CanvasEvents> {
  id: string;
  surface: SurfaceBase;
  children: CanvasObject[] = [];
  sortOrder: string = 'a0'; // https://github.com/rocicorp/fractional-indexing
  protected _interactive: boolean = false;
  protected _visible = true;
  protected cleanupFns: Array<() => void>;

  constructor(id: string, surface: SurfaceBase) {
    super();
    this.id = id;
    this.surface = surface;
    this.children = [];
    this.cleanupFns = [];
  }

  get interactive() {
    return this._interactive;
  }

  set interactive(interactive: boolean) {
    this._interactive = interactive;
  }

  get visible() {
    return this._visible;
  }

  set visible(visible: boolean) {
    this._visible = visible;
  }

  getStore = () => {
    return this.surface.getStore();
  };

  hasChildren = () => {
    return this.children.length > 0;
  };

  addChild = (object: CanvasObject) => {
    this.children.push(object);
  };

  removeChild = (object: CanvasObject) => {
    this.children = filter(this.children, (o) => o.id !== object.id);
  };

  getChildren = () => {
    return [...this.children].sort((a, b) => b.sortOrder.localeCompare(a.sortOrder));
  };

  renderAll = () => {
    this.surface.renderAll();
  };

  refreshScreenCoords() {
    this.children.forEach((c) => {
      c.refreshScreenCoords();
    });
  }

  destroy = () => {
    this.removeAllListeners();
    this.cleanupFns.forEach((fn) => {
      fn();
    });
    this.visible = false;
  };

  getBBoxCoords = (): Point[] => {
    throw new Error('Needs to be implemented by sub class');
  };

  getCenter = (): Point => {
    throw new Error('Needs to be implemented by sub class');
  };

  abstract drawHitArea(ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, color: string): void;

  abstract render(
    ctx: CanvasRenderingContext2D,
    strokeColor?: string,
    strokeWidth?: number,
    isDashed?: boolean,
    strokeOpacity?: number,
    isBorderHidden?: boolean,
  ): void;
}
